import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { InputDefinition } from "../../../../../api/types/inputs";
import { DataFormat } from "../../../../../consts/data-format";
import { CafsVariable } from "../../../../../types/cafs-variable";
import { createOptions } from "../../../../../utils/create-options";
import { useInputDefinitionData } from "../../../api/useInputDefinitionData";
import { CafsVariableType } from "../../../const/cafs-variable-type";

import { cnRatioFn } from "./functions/cnRatio";
import { npkRatioFn } from "./functions/npkRatio";
import { rusleFactorFn } from "./functions/rusleFactor";
import { renderForIrrigation } from "./render-for-irrigation";
import { renderForSeasonal } from "./render-for-seasonal";
import { renderForSeasonalWithSplits } from "./render-for-seasonal-with-splits";

export function useInputDefinitionVariables(
  methods: UseFormReturn,
): CafsVariable<InputDefinition>[] {
  const { data } = useInputDefinitionData(true);

  const baseCategory = methods.watch("Type.Base");

  useEffect(() => {
    if (baseCategory && baseCategory !== "InputSeasonalWithSplits") {
      methods.setValue("FreeNitrogenContent.Base", null);
      methods.setValue("BiomDMN.Base", null);
      methods.setValue("BiomDMP.Base", null);
      methods.setValue("BiomDMK.Base", null);
      methods.setValue("IsMulch.Base", null);
      methods.setValue("CarbonContent.Base", null);
    }

    if (baseCategory && baseCategory === "InputSeasonalWithSplits") {
      methods.resetField("FreeNitrogenContent.Base");
      methods.resetField("BiomDMN.Base");
      methods.resetField("BiomDMP.Base");
      methods.resetField("BiomDMK.Base");
      methods.resetField("IsMulch.Base");
      methods.resetField("CarbonContent.Base");
    }

    if (
      (baseCategory && baseCategory !== "InputSeasonalWithSplits") ||
      baseCategory !== "InputSeasonalNoSplits"
    ) {
      methods.setValue("BiomDMTonnePHaPY.Base", null);
      methods.setValue("DecompositionRate.Base", null);
      methods.setValue("BiomDMTonnePHaPY.Base", null);
    }

    if (
      (baseCategory && baseCategory === "InputSeasonalWithSplits") ||
      baseCategory === "InputSeasonalNoSplits"
    ) {
      methods.resetField("BiomDMTonnePHaPY.Base");
      methods.resetField("DecompositionRate.Base");
      methods.resetField("BiomDMTonnePHaPY.Base");
    }

    if (
      (baseCategory && baseCategory !== "IrrigationSprinkler") ||
      baseCategory !== "IrrigationDrip"
    ) {
      methods.setValue("IrrigationPerMonthMm.Base", null);
    }

    if (
      (baseCategory && baseCategory === "IrrigationSprinkler") ||
      baseCategory === "IrrigationDrip"
    ) {
      methods.resetField("IrrigationPerMonthMm.Base");
    }
  }, [baseCategory]);

  return [
    {
      name: "DefinitionRemarks",
      type: CafsVariableType.Remarks,
    },
    {
      name: "Type",
      type: CafsVariableType.Select,
      options: createOptions(data?.Types),
    },
    {
      name: "Scope",
      type: CafsVariableType.Select,
      options: createOptions(data?.Scopes),
      shouldRender: () => false,
    },
    {
      name: "TriggerEvent",
      type: CafsVariableType.Select,
      options: createOptions(data?.TriggerEvents),
      shouldRender: () => false,
    },
    {
      name: "TriggerInitialMonth",
      type: CafsVariableType.Input,
      shouldRender: () => false,
    },
    {
      name: "EconomicLifeCycle",
      type: CafsVariableType.Input,
      format: DataFormat.Month,
    },
    {
      name: "RuslePFactor",
      type: CafsVariableType.Input,
    },
    {
      name: "RusleCFactor",
      type: CafsVariableType.Input,
    },
    {
      name: "RusleFactor",
      type: CafsVariableType.Calculated,
      dependOn: ["RusleCFactor", "RusleCFactor"],
      calculationFn: rusleFactorFn,
    },
    {
      name: "BiomDMTonnePHaPY",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonal,
    },
    {
      name: "DecompositionRate",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonal,
    },
    {
      name: "BiomDMN",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "BiomDMP",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "BiomDMK",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "FreeNitrogenContent",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "SeasonalInputDMContent",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonal,
    },
    {
      name: "IsMulch",
      type: CafsVariableType.Boolean,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "CarbonContent",
      type: CafsVariableType.Input,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "CNRatio",
      type: CafsVariableType.Calculated,
      dependOn: ["CarbonContent", "BiomDMN"],
      calculationFn: cnRatioFn,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "NPKRatio",
      type: CafsVariableType.Calculated,
      dependOn: ["BiomDMN", "BiomDMP", "BiomDMK", "SeasonalInputDMContent"],
      calculationFn: npkRatioFn,
      shouldRender: renderForSeasonalWithSplits,
    },
    {
      name: "IrrigationPerMonthMm",
      type: CafsVariableType.Input,
      shouldRender: renderForIrrigation,
    },
  ];
}
